/**
 * changeTypes.js
 * ─────────────────────────────────────────────────────────────
 * Constantes, helpers y utilidades relacionadas con los tipos
 * de cambio del wizard CMR.
 *
 * IDs de catálogo (deben coincidir con la API):
 *   1 → Cambio de horarios
 *   2 → Cambio de unidad
 *   3 → Extensión de trazo
 *   4 → Reducción de trazo
 *   5 → Modificar paradas
 */

// ─── IDs DEL CATÁLOGO ─────────────────────────────────────────────────────────

export const CHANGE_IDS = {
    HORARIOS  : 1,
    UNIDAD    : 2,
    EXTENSION : 3,
    REDUCCION : 4,
    PARADAS   : 5,
    FRECUENCIA: 6,
};

// ─── SUB-OPCIONES DE FRECUENCIA ──────────────────────────────────────────────

export const FRECUENCIA_MODOS = {
    MODIFICAR : "modificar",   // Modificar estructura existente
    NUEVA     : "nueva",       // Generar nueva estructura a partir de una existente
    AMBAS     : "ambas",       // Combinación de ambas
};

export const FRECUENCIA_MODO_LABELS = {
    [FRECUENCIA_MODOS.MODIFICAR]: "Modificar estructura existente",
    [FRECUENCIA_MODOS.NUEVA]    : "Generar nueva estructura a partir de una existente",
    [FRECUENCIA_MODOS.AMBAS]    : "Ambas opciones",
};

// ─── ETIQUETAS (mirror del catálogo para uso offline) ─────────────────────────

export const CHANGE_LABELS = {
    [CHANGE_IDS.HORARIOS]  : "Cambio de horarios",
    [CHANGE_IDS.UNIDAD]    : "Cambio de unidad",
    [CHANGE_IDS.EXTENSION] : "Extensión de trazo",
    [CHANGE_IDS.REDUCCION] : "Reducción de trazo",
    [CHANGE_IDS.PARADAS]   : "Modificar paradas",
    [CHANGE_IDS.FRECUENCIA]: "Cambios en la frecuencia",
};


/**
 * Metadata completa por tipo de cambio
 * → Single source of truth
 */
export const CHANGE_META = {
    [CHANGE_IDS.HORARIOS]: {
        label: "Cambio de horarios",
        affectsTrazo: true,
        requiresEstructuras: true,
        showsHorarios: true,
    },
    [CHANGE_IDS.UNIDAD]: {
        label: "Cambio de unidad",
        affectsTrazo: false,
        requiresEstructuras: false,
        showsHorarios: false,
    },
    [CHANGE_IDS.EXTENSION]: {
        label: "Extensión de trazo",
        affectsTrazo: true,
        requiresEstructuras: true,
        showsHorarios: true,
    },
    [CHANGE_IDS.REDUCCION]: {
        label: "Reducción de trazo",
        affectsTrazo: true,
        requiresEstructuras: true,
        showsHorarios: true,
    },
    [CHANGE_IDS.PARADAS]: {
        label: "Modificación de paradas",
        affectsTrazo: true,
        requiresEstructuras: true,
        showsHorarios: false,
    },
    [CHANGE_IDS.FRECUENCIA]: {
        label: "Cambios en la frecuencia",
        affectsTrazo: true,
        requiresEstructuras: false,
        showsHorarios: false,
    },
};

// ─── HELPERS DE SELECCIÓN ─────────────────────────────────────────────────────

/**
 * Obtiene metadata de cambios activos
 */
export const getChangeMeta = (arr = []) =>
    arr
        .filter((id) => CHANGE_META[id])
        .map((id) => ({
            id,
            ...CHANGE_META[id],
        }));

/** ¿El array de tipos seleccionados incluye el id dado? */
export const hasChange = (arr, id) =>
    Array.isArray(arr) && arr.includes(id);

/**
 * ¿Se seleccionó algún tipo que implique editar el trazo (extensión o reducción)?
 * Cuando aplica, al guardar se redirige al editor de trazo.
 */
export const hasTrazoCambio = (arr) =>
    getChangeMeta(arr).some((c) => c.affectsTrazo);

/**
 * ¿Deben mostrarse los campos de horarios en el Paso 2?
 * Aplica cuando se eligió cambio de horarios directo O extensión/reducción
 * (estos dos también permiten ajustar horarios de las estructuras).
 */
export const showHorarios = (arr) =>
    getChangeMeta(arr).some((c) => c.showsHorarios);

/**
 * ¿El tipo de cambio seleccionado requiere elegir estructuras en el Paso 1?
 * Cambio de unidad (id 2) aplica a toda la ruta: no necesita estructuras.
 */
export const needsEstructuras = (arr) =>
    getChangeMeta(arr).some((c) => c.requiresEstructuras);

/** Cambio en la frecuencia*/
export const hasFrecuencia = (changeType) => hasChange(changeType, CHANGE_IDS.FRECUENCIA);

/**
 * ¿El modo de frecuencia requiere modificar estructuras existentes?
 * (modo "modificar" o "ambas")
 */
export const frecuenciaModifica = (modo) =>
    modo === FRECUENCIA_MODOS.MODIFICAR || modo === FRECUENCIA_MODOS.AMBAS;

/**
 * ¿El modo de frecuencia requiere generar copias nuevas?
 * (modo "nueva" o "ambas")
 */
export const frecuenciaGeneraNueva = (modo) =>
    modo === FRECUENCIA_MODOS.NUEVA || modo === FRECUENCIA_MODOS.AMBAS;

/**
 * Retorna labels de cambios activos
 */
export const getChangeLabels = (arr = []) =>
    getChangeMeta(arr).map((c) => c.label);


export const formatList = (items = []) => {
    if (items.length <= 1) return items[0] || "";
    if (items.length === 2) return items.join(" y ");
    return `${items.slice(0, -1).join(", ")} y ${items[items.length - 1]}`;
};


// ─── CONSTRUCTOR DE URL DE TRAZO ──────────────────────────────────────────────

/**
 * Construye la URL del editor de trazo.
 *
 * Un solo tipo de cambio → /cotizaciones/empresariales/:id/trazo/editar/:trazoId/:tipoLabel
 * Más de un tipo de cambio → /cotizaciones/empresariales/:id/trazos/editar/:trazoId/:tipoLabel
 *
 * @param {number|string} cotizacionId
 * @param {number|string} trazoId
 * @param {Array}         structures  – state.estructurasSeleccionadas
 * @returns {string}
 */
export function buildTrazoUrl(cotizacionId, trazoId, structures = []) {

    const plural = structures.length > 1;
    const segmento = plural ? "trazos" : "trazo";
    const tipoLabel = "entrada";

    return `/cotizaciones/empresariales/${cotizacionId}/${segmento}/editar/${trazoId}/${tipoLabel}`;
}

/**
 * Decide qué estructuras se editan directo y cuáles se copian,
 * respetando la acción elegida por estructura en modo AMBAS.
 */
export const getIdsPorAccion = ({ changeType, frecuenciaModo, estructurasSeleccionadas = [], estructurasAccion = {} }) => {
    const esFrec      = hasFrecuencia(changeType);
    const modifica    = esFrec && frecuenciaModifica(frecuenciaModo);
    const generaNueva = esFrec && frecuenciaGeneraNueva(frecuenciaModo);
    const esAmbas     = modifica && generaNueva;
    const accion      = (id) => estructurasAccion[id] ?? "ambas";

    const idsAEditar = estructurasSeleccionadas.filter((id) =>
        !esFrec || (modifica && (!esAmbas || accion(id) !== "copia"))
    );
    const idsParaCopiar = estructurasSeleccionadas.filter((id) =>
        generaNueva && (!esAmbas || accion(id) !== "editar")
    );

    return { idsAEditar, idsParaCopiar };
};